// Site management utilities
// Handles site CRUD operations via Edge Functions
// CHANGE: Added apikey header to all Edge Function fetch calls for gateway compatibility

import { supabase } from './supabase';
import { envConfig } from './envConfig';

function edgeFunctionHeaders(accessToken: string) {
  return {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
    'apikey': envConfig.supabaseAnonKey,
  };
}

export interface Site {
  id: string;
  name: string;
  is_archived: boolean;
  created_at: string;
}

export interface SiteResponse {
  success: boolean;
  error?: string;
}

export interface SitesListResponse {
  success: boolean;
  error?: string;
  data?: Site[];
}

export interface GetUserSitesResponse {
  success: boolean;
  sites?: Site[];
  error?: string;
}

// Create a new site (Admin only) - Uses Edge Function
export async function createSite(name: string): Promise<SiteResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Non authentifié' };
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/manage-sites`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: edgeFunctionHeaders(session.access_token),
      body: JSON.stringify({
        action: 'create',
        name,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Erreur lors de la création du site' };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Update a site (Admin only) - Uses Edge Function
export async function updateSite(siteId: string, name: string): Promise<SiteResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Non authentifié' };
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/manage-sites`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: edgeFunctionHeaders(session.access_token),
      body: JSON.stringify({
        action: 'update',
        siteId,
        name,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Erreur lors de la mise à jour du site' };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Archive a site (Admin only) - Uses Edge Function
export async function archiveSite(siteId: string): Promise<SiteResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Non authentifié' };
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/manage-sites`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: edgeFunctionHeaders(session.access_token),
      body: JSON.stringify({
        action: 'archive',
        siteId,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Erreur lors de l\'archivage du site' };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Restore a site (Admin only) - Uses Edge Function
export async function restoreSite(siteId: string): Promise<SiteResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Non authentifié' };
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/manage-sites`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: edgeFunctionHeaders(session.access_token),
      body: JSON.stringify({
        action: 'restore',
        siteId,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Erreur lors de la restauration du site' };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Get user's assigned sites
export async function getUserSites(userId: string): Promise<GetUserSitesResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Non authentifié' };
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/get-user-sites`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: edgeFunctionHeaders(session.access_token),
      body: JSON.stringify({ userId }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Erreur lors de la récupération des sites' };
    }

    return { success: true, sites: result.sites || [] };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

export async function getActiveSites(): Promise<SitesListResponse> {
  try {
    const { data, error } = await supabase
      .from('sites')
      .select('*')
      .eq('is_archived', false)
      .order('name');

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

export async function getUserSiteCounts(): Promise<{ success: boolean; error?: string; data?: Map<string, number> }> {
  try {
    const { data, error } = await supabase
      .from('user_sites')
      .select('site_id');

    if (error) {
      return { success: false, error: error.message };
    }

    const counts = new Map<string, number>();
    if (data) {
      data.forEach((us: { site_id: string }) => {
        counts.set(us.site_id, (counts.get(us.site_id) || 0) + 1);
      });
    }

    return { success: true, data: counts };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}
